/**
 * D33/D40 (follow-up of P06) — the P06 refusals of an open season, read from
 * an API error.
 *
 * The backend (`TopologyWindowFence::refusal`) answers with the owner's
 * envelope `{ok: false, code, message, errors: {field: [code]}, details:
 * {window_ids}}`, `message` being a Romanian operator sentence:
 *  - 409 `stop_has_active_price_window` — `routes/delete-point`,
 *    `routes/delete`, `countries/delete-city`;
 *  - 409 `fare_category_has_active_price_window` — `price-types/clone`;
 *  - the same fence also answers 409 `pricing_mutation_retry_exhausted` and
 *    `pricing_scope_changed` on those endpoints.
 *
 * `parseSeasonRefusal` returns a CODE-based value (never a sentence): the
 * sentence is chosen in `@/i18n/seasonRefusals`, in the reader's language.
 * `catchErrorMessages` is the robust core of the global `catchErrors`.
 */
import {
  currentSeasonRefusalLocale,
  describeSeasonRefusal,
  translateSeasonRefusal,
  type SeasonRefusalLocale,
} from "@/i18n/seasonRefusals";
import {parseCompanyFeatureRefusal} from "@/utils/companyFeatures";
import {resolveCompanyFeaturesLocale, translateCompanyFeatures} from "@/i18n/companyFeatures";

export const SEASON_REFUSAL_CODES = ["stop_has_active_price_window", "fare_category_has_active_price_window"] as const;
export const FENCE_REFUSAL_CODES = [...SEASON_REFUSAL_CODES, "pricing_mutation_retry_exhausted", "pricing_scope_changed"] as const;

export type FenceRefusalCode = typeof FENCE_REFUSAL_CODES[number];
/** Where the refusal happened: which object was not deleted / created. */
export type RefusalSurface = "stop" | "route" | "city" | "category";

export interface SeasonRefusal {
  /** Unique per occurrence, so the same refusal twice is shown (and announced) twice. */
  key: number;
  code: FenceRefusalCode;
  /** `null` when the surface is unknown (shown through `catchErrors`). */
  surface: RefusalSurface | null;
  /** The open seasons (`details.window_ids`), unique and ascending. */
  windowIds: number[];
  /** The server's own sentence, when it sent a usable one. */
  serverMessage: string | null;
  /** The route to open to close the season, when the surface knows it. */
  busId: number | null;
  /** The name of what was not deleted / created (station, route, city). */
  subjectName: string | null;
}

export interface SeasonRefusalContext {
  busId?: number | null;
  subjectName?: string | null;
}

type Rec = Record<string, unknown>;
const isRecord = (value: unknown): value is Rec => typeof value === "object" && value !== null && !Array.isArray(value);
const MAX_SERVER_MESSAGE = 1000;
let sequence = 0;

/** The response body of an axios-like error, parsed when it arrived as JSON text. */
function responseBody(error: unknown): {hasResponse: boolean; body: unknown} {
  const response = isRecord(error) && isRecord(error.response) ? error.response : null;
  if (!response) return {hasResponse: false, body: undefined};
  let body = response.data;
  if (typeof body === "string") {
    try {
      body = JSON.parse(body);
    } catch (_) {
      // not JSON (an HTML error page, an empty body): keep the text
    }
  }
  return {hasResponse: true, body};
}

const isFenceCode = (value: unknown): value is FenceRefusalCode =>
  typeof value === "string" && (FENCE_REFUSAL_CODES as readonly string[]).includes(value);

function windowIdsOf(body: Rec): number[] {
  const details = isRecord(body.details) ? body.details : isRecord(body.meta) && isRecord((body.meta as Rec).details) ? ((body.meta as Rec).details as Rec) : null;
  const raw = details && Array.isArray(details.window_ids) ? details.window_ids : [];
  const ids = raw.filter((id): id is number => typeof id === "number" && Number.isSafeInteger(id) && id > 0);
  return [...new Set(ids)].sort((a, b) => a - b);
}

function sentenceOf(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const text = value.trim();
  return text !== "" && text.length <= MAX_SERVER_MESSAGE ? text : null;
}

/**
 * The fence refusal carried by `error`, or `null` for anything else
 * (validation, permission, network, another code): the caller keeps its own
 * handling for those.
 */
export function parseSeasonRefusal(error: unknown, surface: RefusalSurface | null, context: SeasonRefusalContext = {}): SeasonRefusal | null {
  const {body} = responseBody(error);
  if (!isRecord(body) || !isFenceCode(body.code)) return null;
  const busId = context.busId;
  return {
    key: ++sequence,
    code: body.code,
    surface,
    windowIds: windowIdsOf(body),
    serverMessage: sentenceOf(body.message),
    busId: typeof busId === "number" && Number.isSafeInteger(busId) && busId > 0 ? busId : null,
    subjectName: sentenceOf(context.subjectName),
  };
}

/** The `field` text of the row with `id` in a page's list (the name shown in the refusal), or `null`. */
export function rowName(rows: unknown, id: unknown, field: string): string | null {
  const row = Array.isArray(rows) ? rows.find((item) => isRecord(item) && item.id === id) : undefined;
  return isRecord(row) ? sentenceOf(row[field]) : null;
}

const escapeHtml = (text: string): string =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");

/**
 * The rows the global `catchErrors` shows for `error` (they are rendered with
 * `v-html` by `SaveItemButtons`, so every text built here is escaped):
 *  - a fence refusal → its readable sentence (+ the seasons), never the code;
 *  - a per-company feature refusal (403 `company_feature_disabled`) → "the
 *    feature is not active for your company", localized;
 *  - a non-empty `errors` object / array → one row per field, EXACTLY as before;
 *    an `errors` that is a plain string → one row, as escaped text (review
 *    P3-3: never HTML; legacy split it into inert characters);
 *  - otherwise the server's `message`, else a localized generic line — a body
 *    without `errors` (409 of an owner, 403/404 `abort`, 500) or no response
 *    at all no longer throws. "The server could not be reached" only for an
 *    axios request that got no response; a JS or dialog error reads neutral
 *    (review P3-2).
 */
export function catchErrorMessages(error: unknown, locale: SeasonRefusalLocale = currentSeasonRefusalLocale()): unknown[] {
  const refusal = parseSeasonRefusal(error, null);
  if (refusal) {
    const view = describeSeasonRefusal(refusal, locale);
    return [escapeHtml(view.seasons ? `${view.text} ${view.seasons}` : view.text)];
  }
  // Per-company feature flag refusal (403 `company_feature_disabled`): one
  // plain, localized sentence instead of a generic error.
  if (parseCompanyFeatureRefusal(error)) {
    return [escapeHtml(translateCompanyFeatures(resolveCompanyFeaturesLocale(locale), "featureDisabled"))];
  }
  const {body} = responseBody(error);
  const errors = isRecord(body) ? body.errors : undefined;
  if (isRecord(errors) && Object.keys(errors).length) return Object.values(errors);
  if (Array.isArray(errors) && errors.length) return errors;
  if (typeof errors === "string" && errors.trim()) return [escapeHtml(errors)];
  const message = isRecord(body) ? sentenceOf(body.message) : null;
  if (message) return [escapeHtml(message)];
  return [translateSeasonRefusal(locale, isUnansweredRequest(error) ? "generic.network" : "generic.error")];
}

/** An axios request that was sent and got no response (offline, DNS, CORS, timeout). */
const isUnansweredRequest = (error: unknown): boolean =>
  isRecord(error) && error.request !== undefined && error.request !== null && !isRecord(error.response);

/**
 * Activates (and focuses) a tab of the route editor — `#tab-<name>` is the
 * header Element Plus renders for `<el-tab-pane name="<name>">` in
 * `route-item.vue`, a shared file this follow-up does not touch. `false` when
 * the tab is not on the page.
 */
export function openRouteEditorTab(name: string): boolean {
  const tab = typeof document === "undefined" ? null : document.getElementById(`tab-${name}`);
  if (!tab) return false;
  tab.click();
  tab.focus();
  return true;
}
